import { createSessionRefresher } from "@dolfin/lib";
import { SESSION_STORAGE_KEY } from "@/lib/constants";

// Apart from AuthContext so the API clients (which it imports) can refresh without an import cycle.

export type ClientType = "Live" | "Sandbox" | "Demo";

export interface UserOrganisation {
  organisationId: string;
  organisationName: string;
  role: string;
  // Not returned by /auth/login; filled lazily from /v1/me. `null` = looked up, unknown; `undefined` = not yet looked up.
  clientId?: string;
  clientType?: ClientType | null;
}

export interface UserClient {
  clientId: string;
  clientName: string;
  type: ClientType;
  role: string;
}

export interface User {
  userId: string;
  email: string;
  name?: string;
  organisations: UserOrganisation[];
  clients: UserClient[];
}

/** Set when the session came from local dev login rather than Google. */
export interface DevPersona {
  key: string;
  label: string;
}

export interface Session {
  token: string;
  user: User;
  activeOrganisationId: string;
  devPersona?: DevPersona | null;
}

export interface AuthApiResponse {
  token: string;
  userId: string;
  email: string;
  organisations: UserOrganisation[];
  clients?: UserClient[];
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const base64 = token.split(".")[1];
    if (!base64) return null;
    return JSON.parse(atob(base64));
  } catch {
    return null;
  }
}

export function userFromAuthResponse(data: AuthApiResponse): User {
  const payload = decodeJwtPayload(data.token);
  return {
    userId: data.userId,
    email: data.email,
    name: payload?.name as string | undefined,
    organisations: data.organisations ?? [],
    clients: data.clients ?? [],
  };
}

export function readStoredSession(): Session | null {
  try {
    const stored = localStorage.getItem(SESSION_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as Session) : null;
  } catch {
    return null;
  }
}

// A refresh re-reads memberships: keep known client types, and the active org while the user still belongs to it.
function applyRefresh(stored: Session | null, response: AuthApiResponse): Session {
  const fresh = userFromAuthResponse(response);
  const known = new Map((stored?.user.organisations ?? []).map((o) => [o.organisationId, o]));
  const organisations = fresh.organisations.map((o) => {
    const prev = known.get(o.organisationId);
    // The response names each org's client type now; older stored sessions fill a gap from what they knew.
    return prev ? { ...o, clientId: prev.clientId, clientType: o.clientType ?? prev.clientType } : o;
  });
  const active = stored?.activeOrganisationId;
  return {
    token: response.token,
    user: { ...fresh, organisations },
    activeOrganisationId: organisations.some((o) => o.organisationId === active)
      ? active!
      : organisations[0]?.organisationId ?? "",
    devPersona: stored?.devPersona ?? null,
  };
}

export const sessionRefresher = createSessionRefresher<Session, AuthApiResponse>({
  storageKey: SESSION_STORAGE_KEY,
  apply: applyRefresh,
});
